import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface CollapsibleCardProps {
  header: ReactNode;      // caller-specific left-side header content
  trailing?: ReactNode;   // caller-specific content immediately before the chevron
  children: ReactNode;    // body rendered when expanded

  // controlled mode — pass both
  expanded?: boolean;
  onToggle?: () => void;

  // uncontrolled mode — pass this instead
  defaultExpanded?: boolean;

  className?: string;
}

export default function CollapsibleCard({
  header,
  trailing,
  children,
  expanded,
  onToggle,
  defaultExpanded = false,
  className,
}: CollapsibleCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const effective = isControlled ? expanded : internalExpanded;
  const handleToggle = () => {
    if (isControlled) onToggle?.();
    else setInternalExpanded((v) => !v);
  };

  return (
    <Card className={`rounded-3xl shadow-ambient gap-0 py-0 overflow-hidden ${className ?? ''}`}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[var(--color-surface-container-low)]"
      >
        <div className="flex-1 min-w-0">{header}</div>
        {trailing}
        {effective ? (
          <ChevronUp className="size-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground shrink-0" />
        )}
      </button>
      {effective && children}
    </Card>
  );
}
